import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  MessageCircle,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const ContatoPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simula envio do formulário
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        assunto: '',
        mensagem: ''
      });
      
      // Limpa o status após 5 segundos
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'E-mail',
      content: 'contato@oxygengroup.com.br',
      link: 'mailto:contato@oxygengroup.com.br',
      color: '#10B981'
    },
    {
      icon: Phone,
      title: 'Telefone',
      content: '(11) 93961-4816',
      link: 'tel:+5511939614816',
      color: '#059669'
    },
    {
      icon: MapPin,
      title: 'Endereço',
      content: 'Avenida dos Navegantes, 560 - Sala 9, Porto Seguro / BA - Brasil',
      link: null,
      color: '#047857'
    },
    {
      icon: Clock,
      title: 'Horário de Atendimento',
      content: 'Seg a Sex: 09h às 18h',
      link: null,
      color: '#065f46'
    }
  ];

  const socialLinks = [
    { icon: Instagram, label: 'Instagram', link: 'https://www.instagram.com/meumei_oficial/', color: '#E4405F' },
    { icon: Facebook, label: 'Facebook', link: 'https://www.instagram.com/meumei_oficial/', color: '#1877F2' },
    { icon: Linkedin, label: 'LinkedIn', link: 'https://www.instagram.com/meumei_oficial/', color: '#0A66C2' },
    { icon: Youtube, label: 'YouTube', link: 'https://www.instagram.com/meumei_oficial/', color: '#FF0000' },
  ];

  const assuntos = [
    'Dúvidas sobre MEI',
    'Suporte Técnico',
    'Parcerias',
    'Financeiro',
    'Outros'
  ];

  return (
    <div className="min-h-screen font-sans">
      {/* Header padronizado */}
      <Header />

      {/* Hero Section */}
      <section 
        className="relative py-32 md:py-40 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
        }}
      >
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          <div 
            className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          {/* Ícones flutuantes */}
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute top-20 right-[20%] opacity-20"
          >
            <MessageCircle size={60} color="white" />
          </motion.div>
          <motion.div
            animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, delay: 1 }}
            className="absolute bottom-32 left-[15%] opacity-20"
          >
            <Mail size={50} color="white" />
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
              className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <MessageCircle size={48} />
            </motion.div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Fale Conosco
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed opacity-90">
              Estamos aqui para ajudar você em sua jornada empreendedora
            </p>
          </motion.div>
        </div>

        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Formulário */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#10B981] font-semibold text-sm uppercase tracking-wider">
                Envie sua mensagem
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2C5E50] mt-2 mb-6">
                Entre em contato
              </h2>
              <p className="text-gray-600 mb-8">
                Preencha o formulário abaixo e nossa equipe retornará o mais breve possível.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all duration-300"
                    placeholder="Seu nome"
                  />
                </div>

                {/* Email e Telefone */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all duration-300"
                      placeholder="seu@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all duration-300"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>

                {/* Assunto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assunto *
                  </label>
                  <select
                    name="assunto"
                    value={formData.assunto}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all duration-300 bg-white"
                  >
                    <option value="">Selecione um assunto</option>
                    {assuntos.map((assunto) => (
                      <option key={assunto} value={assunto}>{assunto}</option>
                    ))}
                  </select>
                </div>

                {/* Mensagem */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 outline-none transition-all duration-300 resize-none"
                    placeholder="Digite sua mensagem..."
                  />
                </div>

                {/* Status de envio */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-4 bg-green-50 text-green-700 rounded-xl"
                  >
                    <CheckCircle size={20} />
                    <span>Mensagem enviada com sucesso! Em breve entraremos em contato.</span>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-xl"
                  >
                    <AlertCircle size={20} />
                    <span>Erro ao enviar mensagem. Tente novamente.</span>
                  </motion.div>
                )}

                {/* Botão de envio */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  className="w-full py-4 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 8px 30px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Enviar Mensagem
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* Informações de contato */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <span className="text-[#10B981] font-semibold text-sm uppercase tracking-wider">
                  Informações
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#2C5E50] mt-2 mb-6">
                  Outras formas de contato
                </h2>
              </div>

              {/* Cards de contato */}
              <div className="grid sm:grid-cols-2 gap-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className="p-6 bg-gray-50 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300 group"
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `linear-gradient(135deg, ${info.color} 0%, ${info.color}dd 100%)`,
                        boxShadow: `0 8px 20px ${info.color}40`,
                      }}
                    >
                      <info.icon className="text-white" size={22} />
                    </div>
                    <h3 className="font-semibold text-[#2C5E50] mb-1">{info.title}</h3>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="text-gray-600 hover:text-[#10B981] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-600">{info.content}</p>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Redes Sociais */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="p-8 bg-gradient-to-br from-gray-50 to-green-50 rounded-2xl"
              >
                <h3 className="font-bold text-[#2C5E50] mb-4">Siga-nos nas redes sociais</h3>
                <p className="text-gray-600 mb-6">
                  Acompanhe nossas novidades e dicas para MEIs
                </p>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.label}
                      href={social.link}
                      whileHover={{ scale: 1.1, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300"
                      style={{
                        background: 'white',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                      }}
                      title={social.label}
                    >
                      <social.icon size={22} style={{ color: social.color }} />
                    </motion.a>
                  ))}
                </div>
              </motion.div>

              {/* FAQ rápido */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="p-8 bg-[#2C5E50] rounded-2xl text-white"
              >
                <h3 className="font-bold text-xl mb-3">Dúvidas Frequentes?</h3>
                <p className="text-white/80 mb-4">
                  Acesse nossa seção de perguntas frequentes para respostas rápidas.
                </p>
                <motion.a
                  href="/#faq"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#2C5E50] font-semibold rounded-xl transition-all"
                >
                  Ver FAQ
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mapa ou CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center bg-white rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{
              boxShadow: '0 20px 60px rgba(16, 185, 129, 0.15)',
            }}
          >
            {/* Decoração */}
            <div 
              className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-30 pointer-events-none"
              style={{
                background: 'radial-gradient(circle, #10B981 0%, transparent 70%)',
                transform: 'translate(30%, -30%)',
              }}
            />

            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
              }}
            >
              <Phone className="text-white" size={32} />
            </motion.div>

            <h2 className="text-3xl md:text-4xl font-bold text-[#2C5E50] mb-4 relative z-10">
              Prefere falar por telefone?
            </h2>
            <p className="text-xl text-gray-600 mb-8 relative z-10">
              Nossa equipe está pronta para atendê-lo de segunda a sexta, das 9h às 18h.
            </p>
            
            <motion.a
              href="tel:+5573998296448"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-10 py-4 text-white font-semibold text-lg rounded-full"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
              }}
            >
              <Phone size={24} />
              (73) 99829-6448
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Footer padronizado */}
      <Footer />
    </div>
  );
};

export default ContatoPage;